// v1.22.0 · Auditoría · Informes redactados con IA (PDF y Word), con gráficos de lo macro a lo micro
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bot, FileDown, FileSpreadsheet, FileText, Loader2, Trash2 } from 'lucide-react';
import { C, SEV, api, download, errMsg, fdate, money, waitJob } from './api';
import { SevTag } from './Panel';

export default function Informes({ cid, analysis, onChanged }) {
  const [reps, setReps] = useState([]);
  const [prior, setPrior] = useState([]);
  const [form, setForm] = useState({ period_label: '', use_ai: true, prior_report_file_id: '' });
  const [job, setJob] = useState(null);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    try {
      setReps(await api.get(`/companies/${cid}/reports`));
      setPrior((await api.get(`/companies/${cid}/files`, { category: 'informes', limit: 100 })).items.filter((f) => f.kind === 'documento'));
    } catch (e) { toast.error(errMsg(e)); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    try {
      const j = await api.post(`/companies/${cid}/reports`, { ...form, prior_report_file_id: form.prior_report_file_id || null });
      setJob(j);
      const done = await waitJob(j.id, setJob, 2000);
      if (done.status === 'done') { toast.success('Informe listo'); load(); onChanged(); } else toast.error(done.message);
    } catch (e) { toast.error(errMsg(e)); }
    setJob(null);
  };
  const open = async (r) => { try { setView(await api.get(`/reports/${r.id}`)); } catch (e) { toast.error(errMsg(e)); } };
  const sc = analysis?.score;

  return (
    <div className="neu-grid" style={{ gap: 18 }}>
      <section className="neu-card">
        <div className="neu-row"><Bot size={20} color={C.brand} /><p className="neu-title" style={{ fontSize: 15 }}>Nuevo informe de auditoría interna</p></div>
        <p className="neu-sub" style={{ marginTop: 4 }}>
          La IA redacta la carta, el resumen ejecutivo, las observaciones (condición, criterio, causa, efecto y recomendaciones) y el seguimiento del
          informe anterior bajo la NIIF para las PYMES. Las cifras, tablas y gráficos salen del sistema, no de la IA. Se incluyen las aclaraciones que
          subió el equipo. Además se generan las hojas de trabajo en Excel (HT-01, HT-02…): una por observación, con la evidencia completa, las
          diferencias como fórmulas, los saldos mes a mes y los asientos del mayor que la respaldan, para revisar de dónde sale cada cifra.
        </p>
        <div className="neu-grid neu-grid-3" style={{ marginTop: 14, gap: 12 }}>
          <label className="neu-field"><span className="neu-label">Período del informe</span>
            <input className="neu-input" placeholder="Automático (ej. «Enero a septiembre 2026»)" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} /></label>
          <label className="neu-field"><span className="neu-label">Informe anterior (guía de estilo y seguimiento)</span>
            <select className="neu-select" value={form.prior_report_file_id} onChange={(e) => setForm({ ...form, prior_report_file_id: e.target.value })}>
              <option value="">El más reciente del expediente</option>{prior.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select></label>
          <label className="neu-row" style={{ fontSize: 13, alignSelf: 'end', paddingBottom: 8 }}>
            <input type="checkbox" checked={form.use_ai} onChange={(e) => setForm({ ...form, use_ai: e.target.checked })} /> Redactar con IA</label>
        </div>
        <div className="neu-row" style={{ marginTop: 14 }}>
          {sc && <span className="neu-hint">Se incluirán {analysis.flags.length} banderas ({sc.open.alta} altas abiertas) y {analysis.conciliations.length} conciliaciones. Puntaje {sc.score} · {sc.label}.</span>}
          <span className="neu-spacer" />
          <button type="button" className="neu-btn neu-btn-primary" onClick={generate} disabled={!!job}>
            {job ? <><Loader2 size={15} className="animate-spin" /> {job.message}</> : <><FileText size={15} /> Generar informe (PDF, Word y hojas de trabajo)</>}</button>
        </div>
        {job && <div className="neu-progress" style={{ marginTop: 12 }}><i style={{ width: `${job.progress || 3}%` }} /></div>}
      </section>

      <section className="neu-card">
        <p className="neu-title">Informes generados ({reps.length})</p>
        <div className="neu-grid" style={{ gap: 10, marginTop: 10 }}>
          {reps.map((r) => (
            <div key={r.id} className="neu-card-flat">
              <div className="neu-row">
                <div style={{ flex: 1, minWidth: 200 }}>
                  <b>{r.period_label}</b>
                  <div className="neu-hint">{fdate(r.created_at)} · {r.created_by} · corte {fdate(r.corte)} · puntaje {r.score?.score} ({r.score?.label}) · {r.model}</div>
                  {r.prior_report && <div className="neu-hint">Guía: {r.prior_report}</div>}
                </div>
                <button type="button" className="neu-btn neu-btn-sm" onClick={() => open(r)}>Ver</button>
                <button type="button" className="neu-btn neu-btn-sm" onClick={() => download(`/reports/${r.id}/pdf`, 'informe.pdf').catch((e) => toast.error(errMsg(e)))}><FileDown size={14} /> PDF</button>
                <button type="button" className="neu-btn neu-btn-sm" onClick={() => download(`/reports/${r.id}/docx`, 'informe.docx').catch((e) => toast.error(errMsg(e)))}><FileDown size={14} /> Word</button>
                {r.xlsx_size ? <button type="button" className="neu-btn neu-btn-sm" title="Papeles de trabajo: una hoja por observación con el detalle de dónde sale cada cifra"
                  onClick={() => download(`/reports/${r.id}/xlsx`, 'hojas_de_trabajo.xlsx').catch((e) => toast.error(errMsg(e)))}><FileSpreadsheet size={14} /> Hojas de trabajo</button> : null}
                <button type="button" className="neu-btn neu-icon-btn neu-btn-danger" aria-label="Borrar informe"
                        onClick={async () => { if (!window.confirm('¿Borrar este informe?')) return; await api.del(`/reports/${r.id}`); load(); }}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {!reps.length && <p className="neu-hint">Todavía no hay informes.</p>}
        </div>
      </section>

      {view && (
        <section className="neu-card" aria-label="Vista del informe">
          <div className="neu-row"><p className="neu-h1" style={{ fontSize: 18 }}>{view.period_label}</p><span className="neu-spacer" />
            <button type="button" className="neu-btn neu-btn-sm" onClick={() => setView(null)}>Cerrar</button></div>
          <p className="neu-label" style={{ marginTop: 14 }}>Resumen ejecutivo</p>
          <ul style={{ margin: '6px 0 0 18px', fontSize: 13.5, lineHeight: 1.55 }}>{(view.content.resumen_ejecutivo || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          {view.content.opinion && <p className="neu-inset" style={{ marginTop: 10, fontSize: 13.5 }}>{view.content.opinion}</p>}
          <p className="neu-label" style={{ marginTop: 16 }}>Observaciones ({(view.content.observaciones || []).length})</p>
          <div className="neu-grid" style={{ gap: 10, marginTop: 8 }}>
            {(view.content.observaciones || []).map((o, i) => (
              <details key={i} className="neu-card-flat">
                <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13.5 }}><SevTag s={o.prioridad} /> Observación {i + 1}. {o.titulo} {o.ref && <span className="neu-chip neu-chip-gray">{o.ref}</span>} <span className="neu-hint">· {o.area}</span></summary>
                <div style={{ fontSize: 13, lineHeight: 1.55, marginTop: 8 }}>
                  {['condicion', 'criterio', 'causa', 'efecto'].map((k) => o[k] && <p key={k} style={{ margin: '4px 0' }}><b style={{ textTransform: 'capitalize' }}>{k === 'condicion' ? 'Condición' : k}:</b> {o[k]}</p>)}
                  {o.recomendaciones?.length > 0 && <ul style={{ margin: '4px 0 0 18px' }}>{o.recomendaciones.map((x, j) => <li key={j}>{x}</li>)}</ul>}
                </div>
              </details>
            ))}
          </div>
          {view.content.seguimiento?.length > 0 && (
            <>
              <p className="neu-label" style={{ marginTop: 16 }}>Seguimiento del informe anterior</p>
              <table className="neu-table" style={{ marginTop: 6 }}><thead><tr><th>Observación anterior</th><th>Estado</th><th>Comentario</th></tr></thead>
                <tbody>{view.content.seguimiento.map((s, i) => <tr key={i}><td>{s.observacion_anterior}</td>
                  <td style={{ color: s.estado === 'superada' ? C.good : s.estado === 'pendiente' ? SEV.alta.color : C.ink2, fontWeight: 700 }}>{s.estado}</td><td>{s.comentario}</td></tr>)}</tbody></table>
            </>
          )}
          <p className="neu-hint" style={{ marginTop: 12 }}>Indicadores del informe: activo {money(view.kpis?.activo)} · resultado {money(view.kpis?.resultado)}.</p>
        </section>
      )}
    </div>
  );
}
